import { createDefaultDetailPageTitleFn, DetailPageLayout } from '@app/DetailPageLayout';
import { useProjectId } from '@config/config.hooks';
import { ApiErrorDisplay } from '@core/ApiErrorDisplay';
import {
  Column,
  createTableCell,
  createTableColumn,
  FormTextField,
  IBreadcrumbItem,
  IconName,
  PaginatedTable,
  requiredRule,
  useForm,
} from '@gcl/react-core';
import {
  ETagManageFormKey,
  IProjectMasterScheduleTagBase,
} from '@masterSchedule/masterScheduleTag/masterScheduleTag.models';
import { useFetchProjectMasterScheduleTagSearch } from '@masterSchedule/masterScheduleTag/masterScheduleTag.queries';
import { useNavigate } from '@routes/routes.hooks';
import { routes } from '@routes/routes.models';
import React from 'react';
import { TagManageRowStateButton } from './MasterScheduleTagManageRowStateButton';

export const MasterScheduleTagManagePage = () => {
  const projectId = useProjectId();
  const { navigate, generateRoutePath, projectNo } = useNavigate();

  const {
    data: dataProjectMasterScheduleTagSearch,
    isLoading: isLoadingProjectMasterScheduleTagSearch,
    isRefetching: isRefetchingProjectMasterScheduleTagSearch,
    error: errorProjectMasterScheduleTagSearch,
  } = useFetchProjectMasterScheduleTagSearch({ projectId });

  const breadcrumbItems: IBreadcrumbItem[] = [
    {
      text: `${projectNo} - Home`,
      prefixIconName: 'House',
      onClick: () => {
        navigate(routes.t1.path);
      },
    },
    {
      text: 'Search T1',
      prefixIconName: 'Search' as IconName,
      onClick: () => {
        navigate(generateRoutePath(routes.t1.path));
      },
    },
    {
      prefixIconName: 'Tag' as IconName,
      text: `Manage T1 Header Tags`,
      active: true,
    },
  ];

  const columns = React.useMemo(
    () => [
      createTableColumn({
        Header: '',
        accessor: 'id',
        width: 30,
        disableResizing: true,
        disableSortBy: true,
        Cell: createTableCell<string, any>(
          (
            value: string,
            item: IProjectMasterScheduleTagBase,
            index: number,
            cellExtras: { [key: string]: any },
            rowExtras,
          ) => {
            // edit / save / delete
            return <TagManageRowStateButton rowExtras={rowExtras} />;
          },
        ),
      } as Column),
      {
        Header: 'Name',
        accessor: 'name',
        disableResizing: false,
        disableSortBy: false,
        Cell: createTableCell<string, any>(
          (
            value: string,
            item: IProjectMasterScheduleTagBase,
            index: number,
            cellExtras: { [key: string]: any },
            rowExtras,
          ) => {
            const { useFormMethods, rowState, setRowState } = rowExtras;
            if (!useFormMethods) return null;
            const { handleSubmit, ...restFormMethods } = useFormMethods;

            return rowState === 'Editing' ? (
              <FormTextField rules={requiredRule} id={ETagManageFormKey.NEW_NAME} formFieldMethods={restFormMethods} />
            ) : (
              <div>{value}</div>
            );
          },
        ),
      } as Column,
    ],
    [],
  );
  return (
    <>
      <ApiErrorDisplay errors={[]} />
      <DetailPageLayout
        isDrawer={false}
        errorGetDetail={errorProjectMasterScheduleTagSearch}
        data={dataProjectMasterScheduleTagSearch}
        isRefetching={isRefetchingProjectMasterScheduleTagSearch}
        isLoading={isLoadingProjectMasterScheduleTagSearch}
        breadcrumb={breadcrumbItems}
        title={createDefaultDetailPageTitleFn(<>Manage T1 Header Tags</>)}
        render={(DetailPageRightMenuOpener) => {
          return (
            <>
              <PaginatedTable
                allowSelectRow={false}
                disablePagination
                columns={columns}
                items={dataProjectMasterScheduleTagSearch ?? []}
                rowExtras={{
                  formCreator: () =>
                    useForm({
                      mode: 'onBlur',
                      shouldFocusError: true,
                    }),
                  formDefaultValues: ({ row, rowIndex }) => {
                    return {
                      callback: () => {
                        const tag = dataProjectMasterScheduleTagSearch?.[rowIndex];

                        return {
                          [ETagManageFormKey.ORIGINAL_NAME]: tag?.name ?? null,
                          [ETagManageFormKey.NEW_NAME]: tag?.name ?? null,
                          [ETagManageFormKey.TAG_TYPE_ID]: tag?.tagTypeId ?? 0,
                        };
                      },
                      deps: [dataProjectMasterScheduleTagSearch],
                    };
                  },
                }}
              />
            </>
          );
        }}
      />
    </>
  );
};
